import React, { FC, useState } from 'react'

import { useApolloClient } from '@apollo/client'

import { Switch } from '@style-port/roov-ui-components-react'
import { Icon, StandardButton, Txt } from 'src/components/atoms'
import { AssignMailCheckbox, CompassRoleDropdown } from 'src/components/molecules'
import { SearchAssignUserInput } from 'src/components/organisms'

import { useToast, useUser } from 'src/hooks'
import { useGetCompassRoles } from 'src/hooks/useGetCompassRoles'
import { ProfileEntity, UserEntity } from 'src/types'

type Props = {
  targetProjectIds: string[]
  profile: ProfileEntity
  selectedUsers: UserEntity[]
  onCancel: () => void
  selectUser: (user: UserEntity) => void
  selectAlreadyUser: (user: UserEntity) => void
  afterAssigned?: () => void
  className?: string
}

export const ProjectsBulkAssign: FC<Props> = ({
  targetProjectIds,
  profile,
  selectedUsers,
  selectAlreadyUser,
  onCancel,
  selectUser,
  afterAssigned,
  className,
}) => {
  const [selectedCompassRoleId, setSelectedCompassRoleId] = useState<number | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [isSendAssignEmail, setIsSendAssignEmail] = useState(false)

  const { assignUsersProjects } = useUser()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { compassRoles, loading: isCompassRoleLoading } = useGetCompassRoles()

  const client = useApolloClient()

  const handleOnClickSubmit = async () => {
    setIsLoading(true)

    const compassRoleId = compassRoles.find(
      compassRole => compassRole.id === selectedCompassRoleId
    )?.id

    if (!compassRoleId) {
      displayErrorTextToast('システム内部のエラーが起きました。再度お試し下さい')
      setIsLoading(false)
      return
    }

    try {
      await assignUsersProjects({
        users: selectedUsers.map(user => ({ id: user.id, isExternal: user.isExternal })),
        projectIds: targetProjectIds,
        isSendAssignEmail,
        compassRoleId,
      })

      // プロジェクト一覧・詳細など広範囲に及ぶ処理のためキャッシュをリセットする。リセットを行うと自動的に画面に必要なqueryが再度実行される
      await client.resetStore()

      displayStandardTextToast('プロジェクトのアサインが完了しました。')

      afterAssigned && afterAssigned()
    } catch {
      displayErrorTextToast('プロジェクトアサイン時にエラーが起きました。')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <SearchAssignUserInput
        alreadyAssignedUserIds={selectedUsers.map(user => user.id)}
        profile={profile}
        onSelectUser={selectedUser => selectUser(selectedUser)}
        className="tw-mt-s0"
      />

      {selectedUsers.length > 0 && (
        <div className="tw-bg-surface-secondary tw-p-s0 tw-rounded-[0.6rem] tw-mt-s2">
          <ul className="tw-flex tw-flex-wrap tw-gap-r-1">
            {selectedUsers.map(user => (
              <li key={user.id} className="tw-w-fit">
                <Switch
                  switchType={user.id === profile.id ? 'correct' : 'primary'}
                  onClick={() => selectAlreadyUser(user)}
                >
                  <div className="tw-flex tw-items-center">
                    <div>{user.name ?? user.email}</div>
                    <Icon path="close/soft/line" size="--r1" className="tw-ml-s-3" />
                  </div>
                </Switch>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-mt-s2">
        <Txt className="tw-text-standardText-secondary">ROOV compassの権限</Txt>
        {!isCompassRoleLoading && (
          <CompassRoleDropdown
            compassRoles={compassRoles}
            value={selectedCompassRoleId}
            selectOption={v => setSelectedCompassRoleId(v)}
            size="m"
            className="tw-ml-s2 tw-w-[17.3rem]"
          />
        )}
      </div>

      <AssignMailCheckbox
        isSendAssignEmail={isSendAssignEmail}
        updateCheck={updateValue => setIsSendAssignEmail(updateValue)}
        className="tw-w-fit tw-mx-auto tw-mt-s3"
      />

      <div className="tw-centering tw-mt-s3">
        <StandardButton
          type="button"
          buttonType="secondary"
          text="キャンセル"
          prependIconPath="close/soft/line"
          size="l"
          onClick={onCancel}
        />
        <StandardButton
          size="l"
          text="決定"
          prependIconPath="check/soft/line"
          disabled={selectedUsers.length === 0 || !selectedCompassRoleId}
          isLoading={isLoading}
          onClick={handleOnClickSubmit}
          className="tw-ml-s2"
        />
      </div>
    </div>
  )
}
